import type { RowData } from "@tanstack/react-table";
import type { DataGridSkeletonPreset } from "../states/skeletonTypes";
import type {
    DataGridAlign,
    DataGridColumn,
    DataGridColumnKind,
    DataGridColumnMeta,
    DataGridResolvedColumnMeta,
} from "./types";

interface ColumnKindDefaults {
    align: DataGridAlign;
    size: number;
    minSize: number;
    isSortable: boolean;
    isResizable: boolean;
    /** Whether cell navigation and ranges can land on the column. */
    isNavigable: boolean;
    /** What the kind's cell shows at rest, before builder options refine it. */
    skeleton: DataGridSkeletonPreset;
    /**
     * Whether the cell's display gives no accessible name of its own (an
     * avatar group), so its edit trigger is named after the column.
     */
    hasTextlessDisplay: boolean;
}

const dataKind = (
    align: DataGridAlign,
    size: number,
    minSize: number,
): ColumnKindDefaults => ({
    align,
    size,
    minSize,
    isSortable: true,
    isResizable: true,
    isNavigable: true,
    skeleton: "text",
    hasTextlessDisplay: false,
});

const displayKind = (size: number): ColumnKindDefaults => ({
    align: "center",
    size,
    minSize: size,
    isSortable: false,
    isResizable: false,
    isNavigable: false,
    skeleton: "none",
    hasTextlessDisplay: false,
});

export const COLUMN_KIND_DEFAULTS: Record<
    DataGridColumnKind,
    ColumnKindDefaults
> = {
    text: dataKind("start", 200, 80),
    number: { ...dataKind("end", 120, 72), skeleton: "number" },
    money: { ...dataKind("end", 140, 96), skeleton: "money" },
    date: { ...dataKind("start", 120, 96), skeleton: "date" },
    status: dataKind("start", 140, 96),
    entity: { ...dataKind("start", 180, 96), skeleton: "avatarText" },
    account: { ...dataKind("start", 200, 120), skeleton: "institutionText" },
    counterparty: dataKind("start", 200, 120),
    fileName: dataKind("start", 240, 120),
    link: dataKind("start", 200, 96),
    person: { ...dataKind("start", 180, 96), skeleton: "institutionText" },
    workspace: { ...dataKind("start", 180, 96), skeleton: "avatarText" },
    people: {
        ...dataKind("start", 140, 72),
        skeleton: "avatarGroup",
        hasTextlessDisplay: true,
    },
    timestamp: { ...dataKind("start", 120, 96), skeleton: "date" },
    dateRange: dataKind("start", 180, 120),
    custom: dataKind("start", 160, 64),
    select: displayKind(40),
    expand: displayKind(40),
    /** Navigable so cell mode can land on the handle and pick the row up. */
    dragHandle: { ...displayKind(40), isNavigable: true },
    /** Navigable so cell mode can land on it and Enter into its controls. */
    actions: { ...displayKind(56), align: "end", isNavigable: true },
    rowNumber: { ...displayKind(56), align: "end", skeleton: "rowNumber" },
};

const metaCache = new WeakMap<object, DataGridResolvedColumnMeta>();

const NO_MENU_ITEMS: DataGridResolvedColumnMeta["menuItems"] = [];

const headerText = <TData extends RowData>(
    columnDef: DataGridColumn<TData>,
): string | undefined =>
    typeof columnDef.header === "string" ? columnDef.header : undefined;

/** Fills every unset meta field with the kind default. */
export const applyMetaDefaults = (
    meta: DataGridColumnMeta,
    fallbackLabel: string,
): DataGridResolvedColumnMeta => {
    const kind = meta.kind ?? "custom";
    return {
        kind,
        align: meta.align ?? COLUMN_KIND_DEFAULTS[kind].align,
        empty: meta.empty ?? "dash",
        overflow: meta.overflow ?? "truncate",
        layout: meta.layout ?? "fit",
        fill: meta.fill,
        fillUntilResized: meta.fillUntilResized ?? false,
        pin: meta.pin,
        showYear: meta.showYear ?? false,
        showPositive: meta.showPositive ?? false,
        tone: meta.tone ?? "primary",
        aggregate: meta.aggregate,
        editor: meta.editor,
        isListEditable: meta.isListEditable ?? false,
        editorPopup: meta.editorPopup,
        skeleton: meta.skeleton ?? COLUMN_KIND_DEFAULTS[kind].skeleton,
        tree: meta.tree ?? false,
        expandAll: meta.expandAll ?? false,
        hasSummary: meta.hasSummary ?? false,
        headerLabel: meta.headerLabel ?? fallbackLabel,
        ariaLabel: meta.ariaLabel,
        menuItems: meta.menuItems ?? NO_MENU_ITEMS,
        variants: meta.variants,
    };
};

/** Applies kind defaults to a column's meta; cached per column definition. */
export const resolveColumnMeta = <TData extends RowData>(
    columnDef: DataGridColumn<TData>,
    columnId: string,
): DataGridResolvedColumnMeta => {
    const cached = metaCache.get(columnDef);
    if (cached) {
        return cached;
    }
    const resolved = applyMetaDefaults(
        columnDef.meta ?? {},
        headerText(columnDef) ?? columnId,
    );
    metaCache.set(columnDef, resolved);
    return resolved;
};
